import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { buildProjectCreditSchema } from '../validation/projectTransactionSchemas';
import { projectService } from '../services/projectService';

export const usePaymentManagement = (project, onUpdate, setToast, onClose) => {
  const [activeTab, setActiveTab] = useState('credit');
  
  const schema = buildProjectCreditSchema(project?.paymentSummary?.netBalance || 0);

  const { register, handleSubmit, watch, setValue, formState: { errors }, setError, clearErrors } = useForm({
    resolver: activeTab === 'credit' ? zodResolver(schema) : undefined,
    mode: 'onChange',
    defaultValues: {
      transactionDate: new Date().toISOString().split('T')[0],
      paymentMethod: 'bank_transfer',
      followUpDate: new Date().toISOString().split('T')[0],
      refId: ''
    }
  });

  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');

  const isRejected = project?.advancePaymentStatus === 'Rejected';

  useEffect(() => {
    if (isRejected) {
      setActiveTab('follow-up');
      if (project?.paymentFollowUpDetails) {
        setValue('reason', project.paymentFollowUpDetails.reason);
        setValue('description', project.paymentFollowUpDetails.notes);
        if (project.paymentFollowUpDetails.approxPaymentDate) {
          setValue('approxPaymentDate', new Date(project.paymentFollowUpDetails.approxPaymentDate).toISOString().split('T')[0]);
        }
      }
    }
  }, [isRejected, project?.paymentFollowUpDetails, setValue]);

  const paymentMethod = watch('paymentMethod');

  const onSubmitCredit = async (data) => {
    setSubmitting(true);
    setServerError('');
    try {
      const amount = parseFloat(data.amount) || 0;
      const totalPaidSoFar = project.paymentSummary?.totalPaid || 0;
      const isFirstPayment = totalPaidSoFar === 0;
      
      // 1. Create the transaction record in the ledger
      await projectService.createProjectTransaction(project._id, {
        kind: 'credit',
        transactionDate: data.transactionDate,
        amount: amount,
        paymentMethod: data.paymentMethod,
        paymentType: data.paymentType || (isFirstPayment ? 'Advance Payment' : 'Balance Payment'),
        refId: (data.refId || '').trim(),
        bankName: data.bankName || '',
        branch: data.branch || '',
        recipient: data.recipient || '',
        internalNotes: data.internalNotes || '',
        // Dynamic fields
        upiPhone: data.upiPhone || '',
        receiverName: data.receiverName || '',
        receiverPhone: data.receiverPhone || '',
        chequeNumber: data.chequeNumber || '',
        chequeDate: data.chequeDate || null
      });

      // 2. Update the project status
      const updateData = {
        advancePaid: (project.advancePaid || 0) + amount,
        advancePaymentStatus: 'Approved',
        paymentFollowUpDetails: {
          reason: isFirstPayment ? 'Initial credit payment' : 'Balance payment',
          amount: amount,
          recordedAt: new Date(),
          notes: data.internalNotes || ''
        }
      };

      // NOTE: Do NOT set status here. The backend (createProjectTransaction)
      // already handles status transitions correctly. Setting status here would
      // overwrite any advanced stage (Documents Approved, Bill Approval, etc.)
      // and revert the project backwards.

      const updatedProject = await projectService.updateProject(project._id, updateData);
      
      if (setToast) setToast({ 
        message: isFirstPayment ? 'Advance payment recorded successfully!' : 'Balance payment recorded and moved to Documents stage!', 
        type: 'success' 
      });
      if (onUpdate) onUpdate(updatedProject);
      if (onClose) onClose();
    } catch (error) {
      const msg = error.response?.data?.message || error.message || 'Failed to record payment.';
      setServerError(msg);
      console.error('Error recording credit:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmitFollowUp = async (data) => {
    try {
      const updatedProject = await projectService.updateProject(project._id, {
        advancePaymentStatus: 'Awaiting Approval',
        paymentFollowUpDetails: {
          reason: `Follow-up: ${data.reason}`,
          approxPaymentDate: data.approxPaymentDate,
          recordedAt: new Date(),
          notes: data.description,
          amount: 0 
        }
      });
      
      if (setToast) setToast({ message: 'Follow-up submitted for owner approval!', type: 'success' });
      if (onUpdate) onUpdate(updatedProject);
      if (onClose) onClose();
    } catch (error) {
      if (setToast) setToast({ message: 'Failed to record follow-up.', type: 'error' });
      console.error('Error recording follow-up:', error);
    }
  };

  return {
    activeTab,
    setActiveTab,
    register,
    handleSubmit,
    setValue,
    errors,
    paymentMethod,
    onSubmitCredit,
    onSubmitFollowUp,
    isRejected,
    submitting,
    serverError
  };
};
